import React, {useMemo, useState} from 'react'
import {useOrganizationAuth} from 'organization/Auth/Providers/OrganizationAuthProvider'
import {NavigateLocalized} from 'i18n/NavigateLocalized'
import {routes} from 'routes/routes'
import {useI18n} from 'i18n/I18nContext'
import {LocalLink} from 'i18n/LocalLink'
import DashboardPageWithoutSideBar from 'organization/theme/DashboardPageWithoutSideBar'
import {toast} from 'react-toastify'
import {LOCALE_LABELS, SUPPORTED_LOCALES, type AppLocale} from 'i18n/locales'
import bg from 'i18n/messages/bg.json'
import en from 'i18n/messages/en.json'
import tr from 'i18n/messages/tr.json'
import de from 'i18n/messages/de.json'
import sv from 'i18n/messages/sv.json'

const BASE: Record<AppLocale, Record<string, string>> = {
  bg: bg as Record<string, string>,
  en: en as Record<string, string>,
  tr: tr as Record<string, string>,
  de: de as Record<string, string>,
  sv: sv as Record<string, string>,
}

const ADMIN_EMAIL = 'abeytulov@gmail.com'

export function TranslationEditor() {
  const {user} = useOrganizationAuth()
  const {
    t,
    translationKeys,
    overrides,
    setOverride,
    resetLocaleOverrides,
    locale,
  } = useI18n()
  const [editLocale, setEditLocale] = useState<AppLocale>(locale)
  const [filter, setFilter] = useState('')

  const allowed = user?.email?.toLowerCase() === ADMIN_EMAIL

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return translationKeys.filter((k) => !f || k.toLowerCase().includes(f))
  }, [translationKeys, filter])

  if (!allowed) {
    return <NavigateLocalized to={routes.organization.list} replace />
  }

  return (
    <DashboardPageWithoutSideBar>
      <div className="row pl-30 pb-30 pr-30">
        <div className="col-12 mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h2 className="mb-1" style={{fontWeight: 700}}>
              {t('lang.editorTitle')}
            </h2>
            <p className="text-muted mb-0" style={{maxWidth: 640}}>
              {t('lang.editorSubtitle')}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <LocalLink to={routes.organization.list} className="fill__btn">
              {t('lang.editorBack')}
            </LocalLink>
          </div>
        </div>

        <div className="col-xxl-12">
          <div className="body__card-wrapper p-4" style={{borderRadius: 12}}>
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label">{t('lang.editorLocale')}</label>
                <select
                  className="form-select"
                  value={editLocale}
                  onChange={(e) => setEditLocale(e.target.value as AppLocale)}
                >
                  {SUPPORTED_LOCALES.map((loc) => (
                    <option key={loc} value={loc}>
                      {LOCALE_LABELS[loc]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Filter</label>
                <input
                  className="form-control"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Key contains…"
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-outline-danger w-100"
                  onClick={() => {
                    resetLocaleOverrides(editLocale)
                    toast.success(t('lang.editorSaved'))
                  }}
                >
                  {t('lang.editorReset')}
                </button>
              </div>
            </div>

            <div
              className="table-responsive"
              style={{maxHeight: '70vh', overflow: 'auto'}}
            >
              <table className="table table-sm align-middle">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{width: '28%'}}>{t('lang.editorKey')}</th>
                    <th>{t('lang.editorValue')}</th>
                    <th style={{width: 120}} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((key) => (
                    <TranslationRow
                      key={`${editLocale}:${key}`}
                      transKey={key}
                      editLocale={editLocale}
                      seed={BASE[editLocale][key] ?? ''}
                      currentOverride={overrides[editLocale]?.[key]}
                      setOverride={setOverride}
                      t={t}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardPageWithoutSideBar>
  )
}

function TranslationRow(props: {
  transKey: string
  editLocale: AppLocale
  seed: string
  currentOverride?: string
  setOverride: (locale: AppLocale, key: string, value: string) => void
  t: (k: string) => string
}) {
  const {transKey, editLocale, seed, currentOverride, setOverride, t} = props
  const [val, setVal] = useState(() => currentOverride ?? seed)

  return (
    <tr>
      <td>
        <code style={{fontSize: 12}}>{transKey}</code>
      </td>
      <td>
        <textarea
          className="form-control form-control-sm"
          rows={2}
          value={val}
          onChange={(e) => setVal(e.target.value)}
        />
      </td>
      <td>
        <button
          type="button"
          className="btn btn-sm btn-primary w-100"
          onClick={() => {
            setOverride(editLocale as any, transKey, val)
            toast.success(t('lang.editorSaved'))
          }}
        >
          {t('common.save')}
        </button>
      </td>
    </tr>
  )
}
